import React, { useEffect, useState, useCallback } from "react";
import axios from "../../api";
import {
  Card,
  TextField,
  FormControl,
  Grid,
  Autocomplete,
  InputAdornment,
  IconButton,
  Button,
  Box,
} from "@mui/material";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { userLogin } from "store/reducers/auth";
import { RootState } from "store";
import Locations from "examples/ReuseFunctions/Locations";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import AWS from "aws-sdk";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { AxiosError } from "axios";

const s3 = new AWS.S3({
  endpoint: process.env.REACT_APP_AWS_ENDPOINT,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
  signatureVersion: "v4",
});

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      "Invalid email format"
    )
    .typeError("Enter a valid email")
    .required("Email is required"),
  phone: Yup.string()
    .matches(/^\d{10}$/, "Enter a valid phone number")
    .required("Phone number is required"),
  password: Yup.string()
    .required("Password is required")
    .min(6, "Password is too short - should be 6 chars minimum.")
    .matches(
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}[\]:;<>,.?~\\/-])/,
      "Password must contain at least one letter, one number, and one special character"
    ),
  confirm_password: Yup.string()
    .oneOf([Yup.ref("password"), undefined], "Passwords must match")
    .required("Confirm Password is required"),

  state_id: Yup.string().required("State is required"),
  city_id: Yup.string().required("City is required"),
  region_id: Yup.string().required("Area is required"),
  address: Yup.string().required("Address is required"),
  address_type: Yup.string().required("Address type is required"),

  type: Yup.string()
    .oneOf(["customer", "company"])
    .required("Customer type required"),
  division_list: Yup.array()
    .of(
      Yup.object({
        division_name: Yup.string().required("Division name is required"),
        branch_code: Yup.string().required("Branch code is required"),
        point_of_contact: Yup.string().nullable(),
        user: Yup.object({
          name: Yup.string().required("Division user name is required"),
          email: Yup.string()
            .email("Invalid email")
            .required("Division user email is required"),
          phone: Yup.string()
            .matches(/^\d{10}$/, "Enter a valid phone number")
            .nullable(),
          password: Yup.string()
            .required("Password is required")
            .min(6, "Password is too short - should be 6 chars minimum.")
            .matches(
              /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}[\]:;<>,.?~\\/-])/,
              "Password must contain at least one letter, one number, and one special character"
            ),
        }).nullable(),
      })
    )
    .when("type", ([type], schema) =>
      type === "company" ? schema.optional() : schema.max(0)
    ),
  company_pan: Yup.string().when("type", ([type], schema) =>
    type === "company"
      ? schema.required("Pan is required")
      : schema.notRequired().nullable()
  ),
  company_gst: Yup.number()
    .typeError("GST must be a number")
    .when("type", ([type], schema) =>
      type === "company"
        ? schema
            .required("GST is required")
            .min(0, "GST cannot be negative")
            .max(10.99, "GST cannot exceed 999.99")
        : schema.notRequired().nullable()
    ),
});

interface UploadedFile {
  name: string;
  type: string;
  size: number;
  preview: string;
}

// Extracted component for Division User Section
interface DivisionUserSectionProps {
  index: number;
  div: any;
  handleChange: any;
  divisionPasswordVisibility: { [key: number]: boolean };
  toggleDivisionPasswordVisibility: (index: number) => void;
  generateDivisionPassword: (setFieldValue: any, index: number) => void;
  setFieldValue: any;
}

const DivisionUserSection: React.FC<DivisionUserSectionProps> = ({
  index,
  div,
  handleChange,
  divisionPasswordVisibility,
  toggleDivisionPasswordVisibility,
  generateDivisionPassword,
  setFieldValue,
}) => {
  const handlePasswordToggle = useCallback(() => {
    toggleDivisionPasswordVisibility(index);
  }, [toggleDivisionPasswordVisibility, index]);

  const handleGeneratePassword = useCallback(() => {
    generateDivisionPassword(setFieldValue, index);
  }, [generateDivisionPassword, setFieldValue, index]);

  return (
    <Box
      sx={{
        mt: 3,
        p: 2,
        backgroundColor: "#f5f5f5",
        borderRadius: 1,
      }}
    >
      <SoftTypography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
        Division User (Login Credentials)
      </SoftTypography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <SoftTypography variant="caption" fontWeight="bold">
              User Name
            </SoftTypography>
            <TextField
              name={`division_list[${index}].user.name`}
              value={div.user?.name || ""}
              onChange={handleChange}
              variant="outlined"
              fullWidth
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <SoftTypography variant="caption" fontWeight="bold">
              User Email
            </SoftTypography>
            <TextField
              name={`division_list[${index}].user.email`}
              value={div.user?.email || ""}
              onChange={handleChange}
              variant="outlined"
              fullWidth
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <SoftTypography variant="caption" fontWeight="bold">
              User Phone
            </SoftTypography>
            <TextField
              name={`division_list[${index}].user.phone`}
              value={div.user?.phone || ""}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              inputProps={{ maxLength: 10 }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <SoftTypography variant="caption" fontWeight="bold">
              User Password
            </SoftTypography>
            <TextField
              name={`division_list[${index}].user.password`}
              type={divisionPasswordVisibility[index] ? "text" : "password"}
              value={div.user?.password || ""}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handlePasswordToggle}>
                      {divisionPasswordVisibility[index] ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            size="small"
            onClick={handleGeneratePassword}
          >
            Generate Password
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

// Extracted component for Division Card
interface DivisionCardProps {
  index: number;
  div: any;
  handleChange: any;
  divisionPasswordVisibility: { [key: number]: boolean };
  toggleDivisionPasswordVisibility: (index: number) => void;
  generateDivisionPassword: (setFieldValue: any, index: number) => void;
  setFieldValue: any;
  onRemove: () => void;
}

const DivisionCard: React.FC<DivisionCardProps> = ({
  index,
  div,
  handleChange,
  divisionPasswordVisibility,
  toggleDivisionPasswordVisibility,
  generateDivisionPassword,
  setFieldValue,
  onRemove,
}) => {
  return (
    <Card
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        boxShadow: 3,
        backgroundColor: "#fff",
      }}
    >
      <SoftTypography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
        Division {index + 1}
      </SoftTypography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <SoftTypography variant="caption" fontWeight="bold">
              Division Name
            </SoftTypography>
            <TextField
              name={`division_list[${index}].division_name`}
              value={div.division_name}
              onChange={handleChange}
              variant="outlined"
              fullWidth
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <SoftTypography variant="caption" fontWeight="bold">
              Branch Code
            </SoftTypography>
            <TextField
              name={`division_list[${index}].branch_code`}
              value={div.branch_code}
              onChange={handleChange}
              variant="outlined"
              fullWidth
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <SoftTypography variant="caption" fontWeight="bold">
              Point of Contact
            </SoftTypography>
            <TextField
              name={`division_list[${index}].point_of_contact`}
              value={div.point_of_contact}
              onChange={handleChange}
              variant="outlined"
              fullWidth
            />
          </FormControl>
        </Grid>
      </Grid>

      <DivisionUserSection
        index={index}
        div={div}
        handleChange={handleChange}
        divisionPasswordVisibility={divisionPasswordVisibility}
        toggleDivisionPasswordVisibility={toggleDivisionPasswordVisibility}
        generateDivisionPassword={generateDivisionPassword}
        setFieldValue={setFieldValue}
      />

      <Grid item sx={{ mt: 2 }}>
        <SoftButton
          className="cancel-btn"
          variant="gradient"
          color="warning"
          onClick={onRemove}
        >
          Remove Division
        </SoftButton>
      </Grid>
    </Card>
  );
};

// New extracted component for Division List
interface DivisionListProps {
  divisionList: any[];
  handleChange: any;
  divisionPasswordVisibility: { [key: number]: boolean };
  toggleDivisionPasswordVisibility: (index: number) => void;
  generateDivisionPassword: (setFieldValue: any, index: number) => void;
  setFieldValue: any;
  onRemove: (index: number) => void;
  onAdd: () => void;
}

const DivisionList: React.FC<DivisionListProps> = ({
  divisionList,
  handleChange,
  divisionPasswordVisibility,
  toggleDivisionPasswordVisibility,
  generateDivisionPassword,
  setFieldValue,
  onRemove,
  onAdd,
}) => {
  return (
    <>
      {divisionList.map((div, index) => {
        const handleRemove = () => onRemove(index);

        return (
          <DivisionCard
            key={`division-${div.division_name}-${div.branch_code}-${index}`}
            index={index}
            div={div}
            handleChange={handleChange}
            divisionPasswordVisibility={divisionPasswordVisibility}
            toggleDivisionPasswordVisibility={toggleDivisionPasswordVisibility}
            generateDivisionPassword={generateDivisionPassword}
            setFieldValue={setFieldValue}
            onRemove={handleRemove}
          />
        );
      })}

      <Button variant="contained" color="primary" onClick={onAdd}>
        Add Division
      </Button>
    </>
  );
};

export interface AddCustomersProps {
  /** When true, render without layout (e.g. inside SlideOverPanel) */
  embedded?: boolean;
  /** Called after successful submit when embedded; when set, used instead of navigate */
  onSuccess?: () => void;
  /** Called when user cancels, when embedded */
  onCancel?: () => void;
}

const AddCustomer = ({
  embedded,
  onSuccess,
  onCancel,
}: AddCustomersProps = {}) => {
  const navigate = useNavigate();
  const { states, cities, areas, setSelectedState, setSelectedCity } =
    Locations();
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { fetchError, fetchSuccess } = useSnackbar();
  const initialLat = 17.4239;
  const initialLng = 78.4738;
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [markerPosition, setMarkerPosition] = useState({
    lat: 17.4239,
    lng: 78.4738,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
  const [divisionPasswordVisibility, setDivisionPasswordVisibility] = useState<{
    [key: number]: boolean;
  }>({});

  const generatePassword = () => {
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*?";
    let password = "P1!";
    for (let i = 0; i < 9; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    return password;
  };

  const dropzone = useDropzone({
    accept: {
      "image/png": [".png", ".jpeg", ".jpg"],
      "video/mp4": [".mp4"],
    },
    onDrop: (acceptedFiles) => {
      setUploadedFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        ),
      ]);
    },
  });

  useEffect(() => {
    const filesWithPreview = dropzone.acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    ) as UploadedFile[];

    setUploadedFiles(filesWithPreview);
  }, [dropzone.acceptedFiles]);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });

  const handleSearchClick = useCallback(() => {
    if (!geocoder || !searchQuery || !map) return;

    geocoder.geocode(
      { address: searchQuery },
      (
        results: google.maps.GeocoderResult[] | null,
        status: google.maps.GeocoderStatus
      ) => {
        if (status === "OK" && results?.[0]) {
          const location = results[0].geometry.location;

          const newMarkerPosition = {
            lat: location.lat(),
            lng: location.lng(),
          };

          setMarkerPosition(newMarkerPosition);
          map.panTo(newMarkerPosition);
        } else {
          alert("Location not found!");
        }
      }
    );
  }, [geocoder, searchQuery, map]);

  const handleSearchByRegion = useCallback(
    (regionName: string) => {
      if (!geocoder) {
        fetchError("Geocoder is not initialized.");
        return;
      }

      geocoder.geocode(
        { address: regionName },
        (
          results: google.maps.GeocoderResult[] | null,
          status: google.maps.GeocoderStatus
        ) => {
          if (status === "OK" && results?.[0]) {
            const location = results[0].geometry.location;

            const newMarkerPosition = {
              lat: location.lat(),
              lng: location.lng(),
            };

            setMarkerPosition(newMarkerPosition);

            if (map) {
              map.panTo(newMarkerPosition);
            } else {
              fetchError("Map is not initialized.");
            }
          } else {
            fetchError("Could not find the location for the selected area.");
          }
        }
      );
    },
    [geocoder, map, fetchError]
  );

  const onMapLoad = useCallback(
    (mapInstance: React.SetStateAction<google.maps.Map | null>) => {
      setMap(mapInstance);
      setGeocoder(new globalThis.google.maps.Geocoder());
      if (searchQuery) {
        handleSearchClick();
      }
    },
    [searchQuery, handleSearchClick]
  );

  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;

    const newMarkerPosition = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };

    setMarkerPosition(newMarkerPosition);
  }, []);

  const uploadFileToS3 = useCallback(
    async (file: UploadedFile, customerId: number) => {
      try {
        const fileName = `${Date.now()}-${file.name}`;
        const bucketName = process.env.REACT_APP_AWS_BUCKET_NAME;

        if (!bucketName) {
          fetchError("AWS bucket configuration is missing");
          return false;
        }

        const params: AWS.S3.PutObjectRequest = {
          Bucket: bucketName,
          Key: fileName,
          Body: file,
          ContentType: file.type,
          ACL: "public-read",
        };

        await s3.upload(params).promise();
        return true;
      } catch (error) {
        console.error("Error uploading file:", error);
        fetchError("Error uploading file");
        return false;
      }
    },
    [fetchError]
  );

  const createAddress = useCallback(
    async (addressData: any, customerId: number) => {
      await axios.post("/address", {
        ...addressData,
        customer_id: customerId,
      });
    },
    []
  );

  const handleSubmit = useCallback(
    async (values: any, { setFieldError }: any) => {
      setSubmitting(true);

      if (
        markerPosition.lat === initialLat &&
        markerPosition.lng === initialLng
      ) {
        setFieldError("latitude", "Please select location on Google Maps");
        setFieldError(
          "longitude",
          "Please select a new location for latitude and longitude."
        );
        fetchError("Please select a new location for latitude and longitude!");
        setSubmitting(false);
        return;
      }

      const customerData = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        password: values.password,
        type: values.type,
        ...(values.type === "company" && {
          divisions:
            values.division_list.length > 0 ? values.division_list : undefined,
          company_pan: values.company_pan,
          company_gst: values.company_gst,
        }),
      };

      const addressData = {
        state_id: values.state_id,
        city_id: values.city_id,
        region_id: values.region_id,
        address: values.address,
        address_type: values.address_type,
        is_primary: 1,
        latitude: markerPosition.lat,
        longitude: markerPosition.lng,
      };

      const subscriptionData = {
        subscription_type: values.subscription_type,
        start_date: values.start_date,
        end_date: values.end_date,
      };

      try {
        const response = await axios.post("/customer", {
          customerData,
          subscriptionData,
        });
        const customerId = response.data.id;
        const isExisting = response.data.isExisting;

        if (!isExisting) {
          await createAddress(addressData, customerId);

          if (uploadedFiles && uploadedFiles.length > 0) {
            await uploadFileToS3(uploadedFiles[0], customerId);
          }
        }

        fetchSuccess(response.data.message);
        try {
          const authRes = await axios.get("/auth");
          dispatch(userLogin({ user: authRes.data }));
        } catch (_) {}
        if (embedded && onSuccess) {
          onSuccess();
        } else {
          navigate("/customers-list");
        }
      } catch (err) {
        if (err instanceof AxiosError && err.response) {
          const msg = err.response.data?.error || "";
          if (
            err.response.status === 403 &&
            (msg.toLowerCase().includes("free tier") || msg.includes("add more customers"))
          ) {
            if (currentUser) {
              dispatch(userLogin({ user: { ...currentUser, freeTierComplete: true } }));
            }
            navigate("/subscribe");
            return;
          }
          fetchError(msg);
        } else {
          fetchError("An error occurred while creating the customer");
        }
      } finally {
        setSubmitting(false);
      }
    },
    [
      markerPosition,
      initialLat,
      initialLng,
      uploadedFiles,
      fetchError,
      fetchSuccess,
      navigate,
      dispatch,
      currentUser,
      createAddress,
      uploadFileToS3,
      embedded,
      onSuccess,
    ]
  );

  const toggleDivisionPasswordVisibility = useCallback((index: number) => {
    setDivisionPasswordVisibility((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  const generateDivisionPassword = useCallback(
    (setFieldValue: any, index: number) => {
      const password = generatePassword();
      setFieldValue(`division_list[${index}].user.password`, password);
    },
    []
  );

  const handleNavigateToCustomersList = useCallback(() => {
    if (embedded && onCancel) {
      onCancel();
    } else {
      navigate("/customers-list");
    }
  }, [navigate, embedded, onCancel]);

  if (!isLoaded) return <div>Loading...</div>;

  const content = (
    <Box sx={{ width: "100%", ...(!embedded && { p: 3, borderRadius: 2, boxShadow: 2 }), ...(embedded && { overflow: "visible", minHeight: "auto" }) }}>
      {!embedded && (
        <SoftTypography variant="h5" style={{ marginBottom: "18px" }}>
          Add New Customer
        </SoftTypography>
      )}
        <Formik
          initialValues={{
            name: "",
            email: "",
            phone: "",
            password: "",
            state_id: "",
            city_id: "",
            region_id: "",
            address: "",
            address_type: "",
            type: "customer",
            division_list: [] as {
              division_name: string;
              branch_code: string;
              point_of_contact: string;
              user: {
                name: string;
                email: string;
                phone: string;
                password: string;
              } | null;
            }[],
            confirm_password: "",
            subscription_type: "",
            start_date: "",
            end_date: "",
            customer_id: "",
            latitude: "",
            longitude: "",
            company_pan: "",
            company_gst: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, handleChange, setFieldValue, values }) => (
            <Form
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <fieldset
                    style={{
                      display: "flex",
                      gap: "20px",
                      marginTop: "8px",
                      border: "none",
                      padding: 0,
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <input
                        type="radio"
                        name="type"
                        value="customer"
                        checked={values.type === "customer"}
                        onChange={handleChange}
                      />
                      <span>Customer</span>
                    </label>

                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <input
                        type="radio"
                        name="type"
                        value="company"
                        checked={values.type === "company"}
                        onChange={handleChange}
                      />
                      <span>Organisation</span>
                    </label>
                  </fieldset>

                  {errors.type && touched.type && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {errors.type}
                    </div>
                  )}
                </FormControl>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      {values.type === "company" ? "Organisation Name" : "Name"}
                    </SoftTypography>
                    <TextField
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.name && touched.name}
                      helperText={touched.name && errors.name}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Phone Number
                    </SoftTypography>
                    <TextField
                      name="phone"
                      value={values.phone}
                      inputProps={{ maxLength: 10 }}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.phone && touched.phone}
                      helperText={touched.phone && errors.phone}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Email
                    </SoftTypography>
                    <TextField
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.email && touched.email}
                      helperText={touched.email && errors.email}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  marginTop="20px"
                  display="flex"
                  justifyContent="center"
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      const password = generatePassword();
                      setFieldValue("password", password);
                      setFieldValue("confirm_password", password);
                    }}
                  >
                    Generate Password
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Password
                    </SoftTypography>
                    <TextField
                      className="view-password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={values.password}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.password && touched.password}
                      helperText={touched.password && errors.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Confirm Password
                    </SoftTypography>
                    <TextField
                      className="view-password"
                      name="confirm_password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={values.confirm_password}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={
                        !!errors.confirm_password && touched.confirm_password
                      }
                      helperText={
                        touched.confirm_password && errors.confirm_password
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                {values.type === "company" && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <SoftTypography variant="caption" fontWeight="bold">
                          Company Pan
                        </SoftTypography>
                        <TextField
                          name="company_pan"
                          value={values.company_pan}
                          inputProps={{ maxLength: 10 }}
                          onChange={handleChange}
                          variant="outlined"
                          fullWidth
                          error={!!errors.company_pan && touched.company_pan}
                          helperText={touched.company_pan && errors.company_pan}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <SoftTypography variant="caption" fontWeight="bold">
                          Company Gst
                        </SoftTypography>
                        <TextField
                          name="company_gst"
                          value={values.company_gst}
                          inputProps={{ maxLength: 10 }}
                          onChange={handleChange}
                          variant="outlined"
                          fullWidth
                          error={!!errors.company_gst && touched.company_gst}
                          helperText={touched.company_gst && errors.company_gst}
                        />
                      </FormControl>
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      State
                    </SoftTypography>
                    <Autocomplete
                      id="state-autocomplete"
                      options={states ?? []}
                      getOptionLabel={(option) => option.state_name || ""}
                      value={
                        (states as unknown as any[])?.find(
                          (state) => state.state_id === values.state_id
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setFieldValue("city_id", "");
                        setFieldValue("region_id", "");
                        setFieldValue(
                          "state_id",
                          newValue ? newValue.state_id : ""
                        );
                        setSelectedState(newValue ? newValue.state_id : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="state_id"
                          variant="outlined"
                          error={!!errors.state_id && touched.state_id}
                          helperText={touched.state_id && errors.state_id}
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      City
                    </SoftTypography>
                    <Autocomplete
                      id="city-autocomplete"
                      options={cities ?? []}
                      getOptionLabel={(option) => option.city_name || ""}
                      value={
                        (cities as unknown as any[])?.find(
                          (city) => city.city_id === values.city_id
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setFieldValue("region_id", "");
                        setFieldValue(
                          "city_id",
                          newValue ? newValue.city_id : ""
                        );
                        setSelectedCity(newValue ? newValue.city_id : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="city_id"
                          variant="outlined"
                          error={!!errors.city_id && touched.city_id}
                          helperText={touched.city_id && errors.city_id}
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Area
                    </SoftTypography>
                    <Autocomplete
                      id="region-autocomplete"
                      options={areas ?? []}
                      getOptionLabel={(option) => option.region_name || ""}
                      value={
                        (areas as unknown as any[])?.find(
                          (city) => city.region_id === values.region_id
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setFieldValue(
                          "region_id",
                          newValue ? newValue.region_id : ""
                        );
                        if (newValue?.region_name) {
                          handleSearchByRegion(newValue.region_name);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="region_id"
                          variant="outlined"
                          error={!!errors.region_id && touched.region_id}
                          helperText={touched.region_id && errors.region_id}
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Address Type
                    </SoftTypography>
                    <TextField
                      name="address_type"
                      value={values.address_type}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.address_type && touched.address_type}
                      helperText={touched.address_type && errors.address_type}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Address
                    </SoftTypography>
                    <TextField
                      name="address"
                      value={values.address}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.address && touched.address}
                      helperText={touched.address && errors.address}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Latitude
                    </SoftTypography>
                    <TextField
                      name="latitude"
                      value={markerPosition.lat}
                      onChange={(e) =>
                        setMarkerPosition((prev) => ({
                          ...prev,
                          lat: Number.parseFloat(e.target.value),
                        }))
                      }
                      variant="outlined"
                      fullWidth
                      error={touched.latitude && Boolean(errors.latitude)}
                      helperText={touched.latitude && errors.latitude}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Longitude
                    </SoftTypography>
                    <TextField
                      name="longitude"
                      value={markerPosition.lng}
                      onChange={(e) =>
                        setMarkerPosition((prev) => ({
                          ...prev,
                          lng: Number.parseFloat(e.target.value),
                        }))
                      }
                      variant="outlined"
                      fullWidth
                      error={touched.longitude && Boolean(errors.longitude)}
                      helperText={touched.longitude && errors.longitude}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  item
                  xs={12}
                  style={{ height: "400px", position: "relative" }}
                >
                  <GoogleMap
                    center={markerPosition}
                    zoom={10}
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    onClick={handleMapClick}
                    onLoad={onMapLoad}
                  >
                    <Marker position={markerPosition} />
                    <Box className="search-Box">
                      <TextField
                        variant="outlined"
                        placeholder="Enter location"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearchClick}
                      >
                        Search
                      </Button>
                    </Box>
                  </GoogleMap>
                </Grid>

                <Grid item xs={12} md={6}>
                  {uploadedFiles.length > 0 && (
                    <div className="upload-media">
                      {uploadedFiles[0]?.preview && (
                        <div
                          className="media-container"
                          style={{ position: "relative" }}
                        >
                          {uploadedFiles[0]?.type.startsWith("image") ? (
                            <img
                              src={uploadedFiles[0]?.preview}
                              alt="media"
                              className="upload-img"
                            />
                          ) : (
                            <video
                              className="upload-video"
                              controls
                              width="100%"
                              height="140px"
                            >
                              <source
                                src={uploadedFiles[0]?.preview}
                                type="video/mp4"
                              />
                              <track
                                kind="captions"
                                src=""
                                label="No captions available"
                                default
                              />
                            </video>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <Box
                    sx={{ cursor: "pointer", width: "fit-content" }}
                    {...dropzone.getRootProps()}
                  >
                    <Box>
                      <label htmlFor="image-upload-input">
                        <input
                          {...dropzone.getInputProps({
                            name: "file_name",
                          })}
                        />
                        <Button
                          component="span"
                          variant="contained"
                          color="primary"
                          className="upload-btn"
                        >
                          Upload File
                        </Button>
                      </label>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              {values.type === "company" && (
                <div>
                  <SoftTypography
                    variant="h6"
                    style={{ marginTop: "20px", marginBottom: "10px" }}
                  >
                    Divisions
                  </SoftTypography>
                  <FieldArray
                    name="division_list"
                    render={({ push, remove }) => (
                      <DivisionList
                        divisionList={values.division_list}
                        handleChange={handleChange}
                        divisionPasswordVisibility={divisionPasswordVisibility}
                        toggleDivisionPasswordVisibility={
                          toggleDivisionPasswordVisibility
                        }
                        generateDivisionPassword={generateDivisionPassword}
                        setFieldValue={setFieldValue}
                        onRemove={remove}
                        onAdd={() =>
                          push({
                            division_name: "",
                            branch_code: "",
                            point_of_contact: "",
                          })
                        }
                      />
                    )}
                  />
                </div>
              )}

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item>
                    <SoftButton
                      className="success-btn"
                      variant="gradient"
                      color="success"
                      type="submit"
                      disabled={submitting}
                    >
                      Save
                    </SoftButton>
                  </Grid>
                  <Grid item>
                    <SoftButton
                      className="cancel-btn"
                      variant="gradient"
                      color="warning"
                      onClick={handleNavigateToCustomersList}
                    >
                      Cancel
                    </SoftButton>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
    </Box>
  );

  if (embedded) {
    return content;
  }
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {content}
    </DashboardLayout>
  );
};

export default AddCustomer;
